import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import PaneBody from 'components/Common/Pane/PaneBody'
import { FormattedMessage, useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'

const TwoColumns = styled.div`
  margin-top: ${rem(20)};

  > * + * {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    columns: 2;

    > * {
      break-inside: avoid;
    }
  }
`

const DetailLine = styled.div`
  margin-top: ${rem(20)};
`

const PaneDetailsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  > * + * {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: ${rem(20)};

    > * + * {
      margin-top: 0;
    }
  }
`

const ViewRoomPhotos = styled.div`
  ${mediaQueryUp.tablet} {
    grid-column-start: 2;
  }
`

function getMaxRoomCapacity(capacities: App.BedbankPackageCapacities) {
  let maxTotal = 0
  let maxAdults = 0
  let maxChildren = 0

  for (const capacity of capacities.combinations) {
    if (capacity.adults > maxAdults) {
      maxAdults = capacity.adults
    }
    if (capacity.children > maxChildren) {
      maxChildren = capacity.children
    }
    const total = capacity.adults + capacity.children
    if (total > maxTotal) {
      maxTotal = total
    }
  }

  return { total: maxTotal, adults: maxAdults, children: maxChildren }
}

interface Props {
  pkg: App.BedbankPackage
  onCarouselToggle?: () => void
}

function BedbankDetailsSection({
  pkg,
  onCarouselToggle,
}: React.PropsWithChildren<Props>) {
  const intl = useIntl()
  const maxCapacities = useMemo(
    () => getMaxRoomCapacity(pkg.capacities),
    [pkg.capacities],
  )

  const aboutTheRoomLabel = intl.formatMessage({
    id: 'T9c5bNYBjhEndCMEMRmR',
    defaultMessage: 'About the room:',
    description: 'Section heading for hotel room details in trip planner',
  })

  const totalGuestsText = intl.formatMessage(
    {
      id: 'Pap1QjNxTnpOOKa5PVso',
      defaultMessage: '{totalCount, plural, one {# guest} other {# guests}}',
      description: 'Total guest capacity count for a hotel room on the room page',
    },
    { totalCount: maxCapacities.total },
  )

  const upToAdultsText = intl.formatMessage(
    {
      id: 'VEDuDELUu_oIeI7dqkfV',
      defaultMessage: 'up to {adultCount, plural, one {# adult} other {# adults}}',
      description: 'Maximum number of adults allowed in a hotel room in the trip planner item editor modal',
    },
    { adultCount: maxCapacities.adults },
  )

  const upToChildrenText = intl.formatMessage(
    {
      id: '77PTp2HR7XV8NqHaQSaq',
      defaultMessage: 'up to {childCount, plural, one {# child} other {# children}}',
      description: 'Maximum number of children allowed in a hotel room',
    },
    { childCount: maxCapacities.children },
  )

  const capacityText = maxCapacities.children
    ? `${totalGuestsText} (${upToAdultsText}, ${upToChildrenText})`
    : totalGuestsText

  const maxCapacityLabel = intl.formatMessage({
    id: '3HSo7j6pgCSD7lYrBbhG',
    defaultMessage: 'Maximum room capacity:',
    description: 'Form field label for maximum room occupancy in trip planner',
  })

  const bedTypeLabel = intl.formatMessage({
    id: 'gMRBLfMeHEg2OdYOvNDr',
    defaultMessage: 'Bed type:',
    description: 'Label for bed type in trip planner hotel room details',
  })

  const roomFacilitiesLabel = intl.formatMessage({
    id: 'YDRTN9_OGgpkpF01tGC7',
    defaultMessage: 'Room facilities:',
    description: 'Section heading for hotel room amenities and facilities',
  })

  const viewRoomPhotosLabel = intl.formatMessage({
    id: 'pRlzVxz4qPy4QA_8_PjG',
    defaultMessage: 'View room photos',
    description: 'Button text to open room photo gallery in trip planner',
  })

  return (
    <PaneBody size="lg xl-y">
      <PaneDetailsWrapper>
        <div>
          <BodyText variant="large" weight="bold">
            {aboutTheRoomLabel}
          </BodyText>
          <DetailLine>
            <BodyText variant="medium" as="span">
              <FormattedMessage
                id="d9wAEKMs6SY7Kplmu6tD"
                defaultMessage="{label} {value}"
                description="Max capacity label and value for bedbank room details"
                values={{
                  label: <BodyText variant="medium" weight="bold" as="span">{maxCapacityLabel}</BodyText>,
                  value: capacityText,
                }}
              />
            </BodyText>
          </DetailLine>
          {pkg.bedGroupsDescription && (
            <DetailLine>
              <BodyText variant="medium" as="span">
                <FormattedMessage
                  id="GSg1Zt4xdJWMYsqEwq_V"
                  defaultMessage="{label} {value}"
                  description="Bed type label and value for bedbank room details in the trip planner item editor modal"
                  values={{
                    label: <BodyText variant="medium" weight="bold" as="span">{bedTypeLabel}</BodyText>,
                    value: pkg.bedGroupsDescription,
                  }}
                />
              </BodyText>
            </DetailLine>
          )}
        </div>
        <div>
          <BodyText variant="large" weight="bold">
            {roomFacilitiesLabel}
          </BodyText>
          <TwoColumns>
            {pkg.facilityGroups.map((group) => (
              <div key={group.name}>
                <BodyText variant="medium" weight="bold">
                  {group.name}
                </BodyText>
                {group.values.map((value) => (
                  <BodyText variant="medium" key={value.name}>
                    {value.name}
                  </BodyText>
                ))}
              </div>
            ))}
          </TwoColumns>
        </div>
        {onCarouselToggle && (
          <ViewRoomPhotos>
            <TextButton
              kind="tertiary"
              horizontalOutdent="start"
              startIcon={<LineCameraIcon />}
              onClick={onCarouselToggle}
            >
              {viewRoomPhotosLabel}
            </TextButton>
          </ViewRoomPhotos>
        )}
      </PaneDetailsWrapper>
    </PaneBody>
  )
}

export default BedbankDetailsSection
